import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './RecipeEdit.css';

const CATEGORIES = ['breakfast','lunch','dinner','appetizers','salads','side-dishes','soups','bread','drinks','desserts'];

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="star-rating">
      {[1,2,3,4,5].map(star => (
        <button key={star} type="button"
          className={`star ${star <= (hover || value) ? 'star--filled' : ''}`}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(star === value ? 0 : star)}>
          ★
        </button>
      ))}
      {value > 0 && <span className="star-label">{value}/5</span>}
    </div>
  );
}

// ── Image Update Lightbox ────────────────────────────────────────────────────
function ImageUpdateModal({ slug, currentImage, onClose, onSuccess }) {
  const [tab, setTab] = useState('upload'); // 'upload' | 'url' | 'auto'
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === modalRef.current) onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Preview selected file
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, WebP, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB');
      return;
    }
    setSelectedFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileChange(fakeEvent);
    }
  };

  // Compress image using canvas before uploading
  // Handles large screenshots from Snipping Tool, mobile camera, etc.
  const compressImage = (file) => new Promise((resolve, reject) => {
    // Timeout safety — if canvas/image load stalls on mobile, reject after 15s
    const timeout = setTimeout(() => reject(new Error('Image processing timed out — try a smaller image')), 15000);

    const reader = new FileReader();
    reader.onerror = () => { clearTimeout(timeout); reject(new Error('Could not read image file: ' + (reader.error?.message || 'unknown error'))); };
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => { clearTimeout(timeout); reject(new Error('Could not decode image — unsupported format or corrupt file')); };
      img.onload = () => {
        try {
          clearTimeout(timeout);
          const MAX = 1200;
          let { width, height } = img;
          // Guard against zero dimensions
          if (!width || !height) { reject(new Error('Image has invalid dimensions (0x0)')); return; }
          if (width > MAX || height > MAX) {
            if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
            else { width = Math.round(width * MAX / height); height = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas not supported on this browser')); return; }
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
          if (!dataUrl || dataUrl === 'data:,') { reject(new Error('Canvas export failed — image may be cross-origin blocked')); return; }
          const base64 = dataUrl.split(',')[1];
          if (!base64) { reject(new Error('Image encoding failed')); return; }
          resolve({ base64, contentType: 'image/jpeg', width, height, originalSize: file.size });
        } catch (e) {
          reject(new Error('Image compression failed: ' + e.message));
        }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });

  // Submit upload
  const handleUploadSubmit = async () => {
    if (!selectedFile) { setError('Please select an image'); return; }
    setSaving(true); setError('');
    try {
      // Compress before sending — handles large screenshots
      let compressed;
      try {
        compressed = await compressImage(selectedFile);
      } catch (compressErr) {
        setError('Could not process image: ' + compressErr.message);
        setSaving(false);
        return;
      }

      const { base64, contentType, width, height, originalSize } = compressed;
      const compressedBytes = Math.round(base64.length * 0.75);
      console.log('Image upload:', { width, height, originalSizeMB: (originalSize/1024/1024).toFixed(2), compressedKB: Math.round(compressedBytes/1024) });

      let res;
      try {
        res = await api.post(`/recipes/${slug}/set-image`, { base64, contentType, source: 'upload' });
      } catch (netErr) {
        const status = netErr.response?.status;
        const serverMsg = netErr.response?.data?.error;
        if (status === 413) {
          setError('Image still too large after compression (' + Math.round(compressedBytes/1024) + 'KB). Try a smaller screenshot.');
        } else if (status === 401 || status === 403) {
          setError('Not authorized — try logging out and back in.');
        } else if (status === 404) {
          setError('Recipe not found. Refresh the page and try again.');
        } else if (status === 500) {
          setError('Server error saving image: ' + (serverMsg || 'unknown. Check server logs.'));
        } else if (!navigator.onLine) {
          setError('No internet connection. Check your network and try again.');
        } else if (netErr.code === 'ERR_NETWORK' || netErr.message?.includes('Network')) {
          setError('Network error — check your connection. (Detail: ' + netErr.message + ')');
        } else {
          setError('Upload failed' + (status ? ' (HTTP ' + status + ')' : '') + ': ' + (serverMsg || netErr.message || 'unknown error'));
        }
        setSaving(false);
        return;
      }

      if (res.data.success) {
        onSuccess(res.data.imagePath);
      } else {
        setError('Server rejected image: ' + (res.data.error || 'unknown reason'));
      }
    } catch (err) {
      setError('Unexpected error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Submit URL
  const handleUrlSubmit = async () => {
    if (!imageUrl.trim()) { setError('Please enter an image URL'); return; }
    if (!imageUrl.match(/^https?:\/\//i)) { setError('URL must start with http:// or https://'); return; }
    setSaving(true); setError('');
    try {
      const res = await api.post(`/recipes/${slug}/set-image`, {
        imageUrl: imageUrl.trim(),
        source: 'url',
      });
      if (res.data.success) {
        onSuccess(res.data.imagePath);
      } else {
        setError(res.data.error || 'Failed to download image');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error;
      if (status === 400) {
        setError(msg || 'Could not download that image URL. Make sure it ends in .jpg, .png, etc.');
      } else if (status === 404) {
        setError('Recipe not found. Refresh the page and try again.');
      } else if (!navigator.onLine) {
        setError('No internet connection.');
      } else {
        setError(msg || 'Failed to download image' + (status ? ' (HTTP ' + status + ')' : '') + '. ' + (err.message || ''));
      }
    } finally {
      setSaving(false);
    }
  };

  // Auto-fetch (existing logic)
  const handleAutoFetch = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.post(`/recipes/${slug}/refresh-image`, {});
      if (res.data.success) {
        onSuccess(res.data.imagePath);
      } else {
        setError('Auto-fetch failed — try uploading an image manually');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Auto-fetch failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="image-modal__backdrop" ref={modalRef} onClick={handleBackdrop}>
      <div className="image-modal">
        <div className="image-modal__header">
          <h3>🖼️ Update Recipe Image</h3>
          <button className="image-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Current image preview */}
        {currentImage && (
          <div className="image-modal__current">
            <span>Current:</span>
            <img src={currentImage} alt="Current recipe" />
          </div>
        )}

        {/* Tabs */}
        <div className="image-modal__tabs">
          <button
            className={`image-modal__tab ${tab === 'upload' ? 'image-modal__tab--active' : ''}`}
            onClick={() => { setTab('upload'); setError(''); }}
          >
            📁 Upload File
          </button>
          <button
            className={`image-modal__tab ${tab === 'url' ? 'image-modal__tab--active' : ''}`}
            onClick={() => { setTab('url'); setError(''); }}
          >
            🔗 Image URL
          </button>
          <button
            className={`image-modal__tab ${tab === 'auto' ? 'image-modal__tab--active' : ''}`}
            onClick={() => { setTab('auto'); setError(''); }}
          >
            ✨ Auto-Fetch
          </button>
        </div>

        <div className="image-modal__body">

          {/* ── Upload tab ── */}
          {tab === 'upload' && (
            <div className="image-modal__panel">
              <p className="image-modal__hint">
                Choose a file, or click <strong>Paste Image</strong> right after taking a snip.
              </p>

              {preview && (
                <div className="image-modal__preview-wrap">
                  <img src={preview} alt="Preview" className="image-modal__preview" />
                  <button className="image-modal__clear-preview" onClick={() => { setSelectedFile(null); setPreview(null); setError(''); }}>
                    ✕ Remove
                  </button>
                </div>
              )}

              {!preview && (
                <div className="image-modal__upload-actions">
                  <button
                    type="button"
                    className="image-modal__upload-btn image-modal__upload-btn--paste"
                    onClick={async () => {
                      setError('');
                      try {
                        const items = await navigator.clipboard.read();
                        for (const item of items) {
                          const imageType = item.types.find(t => t.startsWith('image/'));
                          if (imageType) {
                            const blob = await item.getType(imageType);
                            const file = new File([blob], 'pasted-image.' + imageType.split('/')[1], { type: imageType });
                            setSelectedFile(file);
                            const reader = new FileReader();
                            reader.onload = (ev) => setPreview(ev.target.result);
                            reader.readAsDataURL(file);
                            return;
                          }
                        }
                        setError('No image found in clipboard. Take a snip first, then click Paste Image.');
                      } catch (err) {
                        if (err.name === 'NotAllowedError') {
                          setError('Clipboard access denied. On mobile: use Choose File and pick from your photos instead. On desktop: allow clipboard access in browser settings.');
                        } else if (err.name === 'DataError' || err.message?.includes('No valid data')) {
                          setError('No image in clipboard. Take a screenshot first, then tap Paste Image.');
                        } else if (!navigator.clipboard) {
                          setError('Clipboard not supported on this browser. Use Choose File instead.');
                        } else {
                          setError('Clipboard error (' + err.name + '): ' + err.message + '. Try Choose File instead.');
                        }
                      }
                    }}
                  >
                    <span className="image-modal__upload-btn-icon">📋</span>
                    <span className="image-modal__upload-btn-label">Paste Image</span>
                    <span className="image-modal__upload-btn-sub">from Snipping Tool</span>
                  </button>
                  <button
                    type="button"
                    className="image-modal__upload-btn image-modal__upload-btn--file"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleDrop}
                  >
                    <span className="image-modal__upload-btn-icon">📁</span>
                    <span className="image-modal__upload-btn-label">Choose File</span>
                    <span className="image-modal__upload-btn-sub">JPG, PNG, WebP</span>
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              {error && <p className="image-modal__error">{error}</p>}
              <button
                className="btn btn-primary image-modal__submit"
                onClick={handleUploadSubmit}
                disabled={saving || !selectedFile}
              >
                {saving ? '⏳ Uploading...' : '✅ Use This Image'}
              </button>
            </div>
          )}

          {/* ── URL tab ── */}
          {tab === 'url' && (
            <div className="image-modal__panel">
              <p className="image-modal__hint">
                Paste a direct link to any image. The image will be downloaded and saved to your recipe.
              </p>
              <input
                type="url"
                className="image-modal__url-input"
                value={imageUrl}
                onChange={e => { setImageUrl(e.target.value); setError(''); }}
                placeholder="https://example.com/photo.jpg"
                autoCapitalize="none"
                autoCorrect="off"
              />
              {imageUrl && imageUrl.match(/^https?:\/\//i) && (
                <img
                  src={imageUrl}
                  alt="URL preview"
                  className="image-modal__url-preview"
                  onError={e => { e.target.style.display = 'none'; }}
                  onLoad={e => { e.target.style.display = 'block'; }}
                />
              )}
              {error && <p className="image-modal__error">{error}</p>}
              <button
                className="btn btn-primary image-modal__submit"
                onClick={handleUrlSubmit}
                disabled={saving || !imageUrl.trim()}
              >
                {saving ? '⏳ Downloading...' : '✅ Use This Image'}
              </button>
            </div>
          )}

          {/* ── Auto-fetch tab ── */}
          {tab === 'auto' && (
            <div className="image-modal__panel">
              <p className="image-modal__hint">
                Automatically search for an image using the recipe title.
                Works best for common dishes — results may vary.
              </p>
              <div className="image-modal__auto-note">
                💡 <strong>Tip:</strong> For the best results, use <strong>Upload File</strong> to take a photo
                of the finished dish, or use <strong>Image URL</strong> to paste the image from the original recipe page.
              </div>
              {error && <p className="image-modal__error">{error}</p>}
              <button
                className="btn btn-primary image-modal__submit"
                onClick={handleAutoFetch}
                disabled={saving}
              >
                {saving ? '⏳ Searching...' : '✨ Auto-Find Image'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Main RecipeEdit Component ────────────────────────────────────────────────
export default function RecipeEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pageJustLoaded, setPageJustLoaded] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [totalTime, setTotalTime] = useState('');
  const [servings, setServings] = useState('');
  const [servingsUnit, setServingsUnit] = useState('');
  const [ingredients, setIngredients] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [chefNotes, setChefNotes] = useState('');
  const [personalNotes, setPersonalNotes] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [rating, setRating] = useState(0);

  const formLoadedRef = useRef(false);

  useEffect(() => {
    api.get(`/recipes/${slug}`)
      .then(res => {
        const r = res.data.recipe;
        setRecipe(r);
        setTitle(r.title || '');
        setDescription(r.description || '');
        setCategory(r.category_slug || '');
        setDifficulty(r.difficulty || '');
        setPrepTime(r.prep_time_mins || '');
        setCookTime(r.cook_time_mins || '');
        setTotalTime(r.total_time_mins || '');
        setServings(r.servings || '');
        setServingsUnit(r.servings_unit || '');
        setIngredients(r.ingredients || []);
        setInstructions(r.instructions || []);
        setChefNotes(r.chef_notes || '');
        setPersonalNotes(r.personal_notes || '');
        setTagsInput((r.tags || []).join(', '));
        setRating(r.rating || 0);
        setTimeout(() => { formLoadedRef.current = true; setPageJustLoaded(false); }, 800);
      })
      .catch(() => setError('Failed to load recipe'))
      .finally(() => setLoading(false));
  }, [slug]);

  const markDirty = () => {
    if (formLoadedRef.current) setIsDirty(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMessage(''); setError('');
    try {
      const tags = tagsInput.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      await api.patch(`/recipes/${slug}/edit`, {
        title, description, category, difficulty,
        prep_time_mins: prepTime ? parseInt(prepTime) : null,
        cook_time_mins: cookTime ? parseInt(cookTime) : null,
        total_time_mins: totalTime ? parseInt(totalTime) : null,
        servings: servings ? parseInt(servings) : null,
        servings_unit: servingsUnit,
        ingredients, instructions, chef_notes: chefNotes,
        tags, rating: (rating && rating > 0) ? rating : null, personal_notes: personalNotes
      });
      setMessage('Recipe saved!');
      setIsDirty(false);
      setTimeout(() => navigate(`/recipe/${slug}`), 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete "${recipe?.title}"?\n\nThis cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/recipes/${slug}`);
      navigate('/', { replace: true });
    } catch (err) {
      setError('Failed to delete: ' + (err.response?.data?.error || err.message));
      setDeleting(false);
    }
  };

  const handleImageSuccess = (newImagePath) => {
    setRecipe(prev => ({ ...prev, primary_image: newImagePath }));
    setIsDirty(false);
    setShowImageModal(false);
    setMessage('✅ Image updated successfully!');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Ingredient helpers
  const updateIngredient = (i, field, val) => {
    const updated = [...ingredients];
    updated[i] = { ...updated[i], [field]: val };
    setIngredients(updated);
    markDirty();
  };
  const addIngredient = () => { setIngredients([...ingredients, { amount: '', unit: '', name: '', notes: '' }]); markDirty(); };
  const removeIngredient = (i) => { setIngredients(ingredients.filter((_, idx) => idx !== i)); markDirty(); };

  // Instruction helpers
  const updateInstruction = (i, val) => {
    const updated = [...instructions];
    updated[i] = { ...updated[i], step: i + 1, text: val };
    setInstructions(updated);
  };
  const addInstruction = () => { setInstructions([...instructions, { step: instructions.length + 1, text: '' }]); markDirty(); };
  const removeInstruction = (i) => {
    const updated = instructions.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step: idx + 1 }));
    setInstructions(updated);
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;
  if (error && !recipe) return <div className="error-state container"><h2>{error}</h2></div>;

  const currentImageUrl = recipe?.primary_image
    ? (recipe.primary_image.startsWith('http') ? recipe.primary_image : `https://recipe.marumba.com${recipe.primary_image}`)
    : null;

  return (
    <div className="recipe-edit container">

      {/* Image update lightbox */}
      {showImageModal && (
        <ImageUpdateModal
          slug={slug}
          currentImage={currentImageUrl}
          onClose={() => setShowImageModal(false)}
          onSuccess={handleImageSuccess}
        />
      )}

      <div className="recipe-edit__header">
        <div>
          <h1>Edit Recipe</h1>
          <p>{recipe?.title}</p>
        </div>
        <div className="recipe-edit__header-actions">
          <button className="btn btn-ghost" onClick={() => navigate(`/recipe/${slug}`)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? <span className="spinner" /> : '💾 Save Changes'}
          </button>
        </div>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSave} className="recipe-edit__form">

        {/* Basic Info */}
        <div className="recipe-edit__section">
          <h2>Basic Information</h2>
          <div className="recipe-edit__grid-2">
            <div className="form-group" style={{gridColumn: '1/-1'}}>
              <label>Recipe Title</label>
              <input type="text" value={title} onChange={e => { setTitle(e.target.value); markDirty(); }} required />
            </div>
            <div className="form-group" style={{gridColumn: '1/-1'}}>
              <label>Description</label>
              <textarea value={description} onChange={e => { setDescription(e.target.value); markDirty(); }} rows={3} />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={category} onChange={e => { setCategory(e.target.value); markDirty(); }}>
                <option value="">Select category...</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select value={difficulty} onChange={e => { setDifficulty(e.target.value); markDirty(); }}>
                <option value="">Select difficulty...</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
        </div>

        {/* Time & Servings */}
        <div className="recipe-edit__section">
          <h2>Time & Servings</h2>
          <div className="recipe-edit__grid-4">
            <div className="form-group">
              <label>Prep Time (mins)</label>
              <input type="number" value={prepTime} onChange={e => { setPrepTime(e.target.value); markDirty(); }} min="0" />
            </div>
            <div className="form-group">
              <label>Cook Time (mins)</label>
              <input type="number" value={cookTime} onChange={e => { setCookTime(e.target.value); markDirty(); }} min="0" />
            </div>
            <div className="form-group">
              <label>Total Time (mins)</label>
              <input type="number" value={totalTime} onChange={e => { setTotalTime(e.target.value); markDirty(); }} min="0" />
            </div>
            <div className="form-group">
              <label>Servings</label>
              <input type="number" value={servings} onChange={e => { setServings(e.target.value); markDirty(); }} min="1" />
            </div>
            <div className="form-group">
              <label>Servings Unit</label>
              <input type="text" value={servingsUnit} onChange={e => { setServingsUnit(e.target.value); markDirty(); }} placeholder="servings, pieces..." />
            </div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="recipe-edit__section">
          <div className="recipe-edit__section-header">
            <h2>Ingredients</h2>
            <button type="button" className="btn btn-outline" onClick={addIngredient}>+ Add Ingredient</button>
          </div>
          <div className="recipe-edit__ingredients">
            <div className="recipe-edit__ing-header">
              <span>Amount</span><span>Unit</span><span>Ingredient</span><span>Notes</span><span></span>
            </div>
            {ingredients.map((ing, i) => (
              <div key={i} className="recipe-edit__ing-row">
                <input type="text" value={ing.amount || ''} onChange={e => updateIngredient(i, 'amount', e.target.value)} placeholder="2" />
                <input type="text" value={ing.unit || ''} onChange={e => updateIngredient(i, 'unit', e.target.value)} placeholder="cups" />
                <input type="text" value={ing.name || ''} onChange={e => updateIngredient(i, 'name', e.target.value)} placeholder="flour" />
                <input type="text" value={ing.notes || ''} onChange={e => updateIngredient(i, 'notes', e.target.value)} placeholder="sifted" />
                <button type="button" className="recipe-edit__remove" onClick={() => removeIngredient(i)}>✕</button>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="recipe-edit__section">
          <div className="recipe-edit__section-header">
            <h2>Instructions</h2>
            <button type="button" className="btn btn-outline" onClick={addInstruction}>+ Add Step</button>
          </div>
          <div className="recipe-edit__instructions">
            {instructions.map((step, i) => (
              <div key={i} className="recipe-edit__step-row">
                <span className="recipe-edit__step-num">{i + 1}</span>
                <textarea
                  value={step.text || ''}
                  onChange={e => updateInstruction(i, e.target.value)}
                  rows={2}
                  placeholder={`Step ${i + 1}...`}
                />
                <button type="button" className="recipe-edit__remove" onClick={() => removeInstruction(i)}>✕</button>
              </div>
            ))}
          </div>
        </div>

        {/* Notes & Tags */}
        <div className="recipe-edit__section">
          <h2>Notes & Tags</h2>
          <div className="recipe-edit__grid-2">
            <div className="form-group">
              <label>Chef's Notes</label>
              <textarea value={chefNotes} onChange={e => { setChefNotes(e.target.value); markDirty(); }} rows={4}
                placeholder="Tips, variations, substitutions..." />
            </div>
            <div className="form-group">
              <label>My Personal Notes</label>
              <textarea value={personalNotes} onChange={e => { setPersonalNotes(e.target.value); markDirty(); }} rows={4}
                placeholder="Your private notes about this recipe..." />
            </div>
            <div className="form-group" style={{gridColumn: '1/-1'}}>
              <label>Tags <span style={{color:'var(--color-text-muted)', fontWeight:400}}>(comma separated)</span></label>
              <input type="text" value={tagsInput} onChange={e => { setTagsInput(e.target.value); markDirty(); }}
                placeholder="chicken, air fryer, quick, one-pot..." />
            </div>
          </div>
        </div>

        {/* Rating */}
        <div className="recipe-edit__section">
          <h2>My Rating</h2>
          <StarRating value={rating} onChange={(v) => { setRating(v || 0); markDirty(); }} />
        </div>

        {/* Save button at bottom */}
        <div className="recipe-edit__footer">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(`/recipe/${slug}`)}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !isDirty}>
            {saving ? <span className="spinner" /> : '💾 Save Changes'}
          </button>
        </div>
      </form>

      {/* ── Danger Zone ─────────────────────────────────────────── */}
      <div className="recipe-edit__danger-zone">
        <h3>⚠️ Danger Zone</h3>
        <div className="recipe-edit__danger-actions">

          {/* Update Image */}
          <div className="recipe-edit__danger-item">
            <div>
              <strong>Update Image</strong>
              <p>Upload a photo, paste an image URL, or auto-search for a matching food photo.</p>
            </div>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowImageModal(true)}
            >
              🖼️ Update Image
            </button>
          </div>

          {/* Delete Recipe */}
          <div className="recipe-edit__danger-item recipe-edit__danger-item--delete">
            <div>
              <strong>Delete Recipe</strong>
              <p>Permanently remove this recipe from your collection. This cannot be undone.</p>
            </div>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? '⏳ Deleting...' : '🗑️ Delete Recipe'}
            </button>
          </div>

        </div>
      </div>

    </div>
  );
}
